import { LaneItem, LevelManager } from '../LevelManager';
import { VehicleManager } from './VehicleManager';
import { DriverProfile, Vehicle } from './Vehicle';
import { EntityType } from '../level/entities/Entities';
import { VehicleVillain } from './VehicleVillain';
import CharacterSpine, { CharacterAnimation } from './CharacterSpine';
import { SpineEvents } from '../../core/enums/SpineEvents';
import { LevelUtil } from './../level/levels/LevelUtil';
import { LootStage } from '../level/levels/level';
import { RampStage } from './../level/levels/level';
import { VehicleEquipmentWeapon } from './VehicleEquipmentWeapon';
import { LevelFeature } from '../level/LevelFeature';
import { RampFeature } from './../level/entities/RampFeature';
import { RingFeature } from './../level/entities/RingFeature';

export const MoveDirection = {
  Up: 'up',
  Down: 'down'
} as const;
type MoveDirectionKeys = keyof typeof MoveDirection;
type MoveDirectionValues = typeof MoveDirection[MoveDirectionKeys];

export const ActionPhase = {
  Default: 'default',
  Intro: 'intro',
  LootDrop: 'lootDrop',
  Ramp: 'ramp',
  Capture: 'capture'
};
type ActionPhaseKeys = keyof typeof ActionPhase;
type ActionPhaseValues = typeof ActionPhase[ActionPhaseKeys];

// export interface State {
//   next: () => void;
//   enter: (args: any) => void;
//   exit: (args: any) => void;
//   update: (time: number, delta: number) => void;
// }

export class AIBoss {
  public static events = {
    StartedSpawningLoot: 'startedSpawningLoot',
    StoppedSpawningLoot: 'stoppedSpawningLoot'
  } as const;

  protected scene: Phaser.Scene;
  protected vehicle: VehicleVillain;
  protected vehicleManager: VehicleManager;
  protected levelManager: LevelManager;

  protected phase: ActionPhaseValues;
  protected isPhaseInProgress: boolean;

  protected shouldFollowTarget: boolean;
  protected shouldMirrorPlayer: boolean;
  protected shouldEvade: boolean;
  protected minMoveTime: number;
  protected maxMoveTime: number;
  protected moveDirection: MoveDirectionValues;
  protected moveTimer: number;

  protected canSpawnLoot: boolean;
  protected spawnTime: number;
  protected spawnTimer: number;
  protected quantityToSpawn: number;
  protected moneyBagInterval: number;
  protected spawnedCount: number;
  protected spawnedTotal: number;

  protected shouldVillainUseTrapWhileDropping?: boolean;
  protected shouldTriggerOnDrops: boolean;
  protected weaponTriggerPoints: number[];
  protected weaponTriggerPointIndex: number;
  protected canFireWeapon: boolean;
  protected weaponIsPreparingToFire: boolean;
  protected weaponIsFiring: boolean;
  protected weaponCooldownTime: number;
  protected weaponCooldown: number;

  // Ramp
  protected hasJumped: boolean;

  constructor(scene: Phaser.Scene, vehicle: VehicleVillain, vehicleManager: VehicleManager, levelManager: LevelManager) {
    this.scene = scene;
    this.vehicle = vehicle;
    this.vehicleManager = vehicleManager;
    this.levelManager = levelManager;

    this.phase = ActionPhase.Default;
    this.isPhaseInProgress = false;

    // Movement
    this.shouldFollowTarget = false;
    this.shouldMirrorPlayer = false;
    this.shouldEvade = false;
    this.minMoveTime = 1000;
    this.maxMoveTime = 5000;
    this.moveDirection = MoveDirection.Up;
    this.moveTimer = 0;

    // Loot
    this.canSpawnLoot = false;
    this.spawnTime = 0;
    this.spawnTimer = this.spawnTime;
    this.quantityToSpawn = 0;
    this.moneyBagInterval = 0;
    this.spawnedCount = 0;
    this.spawnedTotal = 0;

    // Weapon
    this.shouldVillainUseTrapWhileDropping = false;
    this.shouldTriggerOnDrops = true;
    this.weaponTriggerPoints = [];
    this.weaponTriggerPointIndex = 0;
    this.canFireWeapon = false;
    this.weaponIsPreparingToFire = false;
    this.weaponIsFiring = false;
    this.weaponCooldownTime = 5000;
    this.weaponCooldown = this.weaponCooldownTime;

    // Ramp
    this.hasJumped = false;

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.vehicle.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);

      this.vehicle.Weapon?.off(VehicleEquipmentWeapon.events.Firing, this.onWeaponFiring, this);
      this.vehicle.Weapon?.off(VehicleEquipmentWeapon.events.FinishedFiring, this.onWeaponFinishedFiring, this);
      // this.vehicle.CharacterSpine.off(SpineEvents.Start, this.animationStart, this);
      // this.vehicle.CharacterSpine.off(SpineEvents.End, this.animationEnd, this);

      this.vehicle.CharacterSpine.off(CharacterSpine.events.RampInEnded, this.dropSuperMoneyBag, this);
      this.vehicle.CharacterSpine.off(CharacterSpine.events.RampOutEnded, this.onRampJumpEnded, this);
      this.vehicle.off(Vehicle.events.RingEnter, this.dropSuperMoneyBagRing, this);
      this.vehicle.off(Vehicle.events.RingExit, this.onRampJumpEnded, this);
    });

    this.vehicle.Weapon?.on(VehicleEquipmentWeapon.events.Firing, this.onWeaponFiring, this);
    this.vehicle.Weapon?.on(VehicleEquipmentWeapon.events.FinishedFiring, this.onWeaponFinishedFiring, this);
    // this.vehicle.CharacterSpine.on(SpineEvents.Start, this.animationStart, this);
    // this.vehicle.CharacterSpine.on(SpineEvents.End, this.animationEnd, this);
  }

  public dropLoot(lootStage: LootStage) {
    this.phase = ActionPhase.LootDrop;
    this.isPhaseInProgress = true;

    this.spawnTime = lootStage.dropRateMin;
    this.spawnTimer = this.spawnTime;
    this.quantityToSpawn = lootStage.dropQuantity;
    this.moneyBagInterval = lootStage.specialDropInterval;
    this.spawnedCount = 0;
    this.spawnedTotal = 0;

    this.shouldVillainUseTrapWhileDropping = lootStage.shouldVillainUseTrapWhileDropping;
    this.shouldTriggerOnDrops = lootStage.shouldTriggerOnDrops;
    this.weaponTriggerPoints = lootStage.weaponTriggerPoints;
    this.weaponTriggerPointIndex = 0;

    this.canSpawnLoot = true;
    this.canFireWeapon = true;
    this.shouldEvade = true;
    this.shouldMirrorPlayer = false;

    // this.vehicle.setCameraDistance(450);
    // this.vehicle.toggleTrackX(true);

    this.scene.events.emit(AIBoss.events.StartedSpawningLoot);
  }

  public takeRamp(rampStage: RampStage, feature: LevelFeature) {
    this.phase = ActionPhase.Ramp;
    this.isPhaseInProgress = true;

    this.shouldEvade = false;
    this.shouldMirrorPlayer = false;
    this.shouldFollowTarget = true;

    this.hasJumped = false;

    this.moveToY(feature.getRampEntry());

    if (feature instanceof RampFeature) {
      this.vehicle.CharacterSpine.once(CharacterSpine.events.RampInEnded, this.dropSuperMoneyBag, this);
      this.vehicle.CharacterSpine.once(CharacterSpine.events.RampOutEnded, this.onRampJumpEnded, this);
    } else if (feature instanceof RingFeature) {
      this.vehicle.once(Vehicle.events.RingEnter, this.dropSuperMoneyBagRing, this);
      this.vehicle.once(Vehicle.events.RingExit, this.onRampJumpEnded, this);
    }
  }

  public moveToY(y: number) {
    this.vehicle.moveToY(y);
  }

  public toggleEvasiveBehaviour(shouldEvade: boolean) {
    this.shouldEvade = shouldEvade;
  }

  public toggleLootDropping(shouldSpawnLoot: boolean) {
    this.canSpawnLoot = shouldSpawnLoot;
  }

  public toggleWeapon(shouldUseWeapon: boolean) {
    this.canFireWeapon = shouldUseWeapon;
  }

  public toggleMirrorHero(shouldMirrorPlayer: boolean) {
    this.shouldMirrorPlayer = shouldMirrorPlayer;
  }

  public update(time: number, delta: number) {
    if (!this.isPhaseInProgress) {
      if (this.phase == ActionPhase.Default) {
        this.shouldFollowTarget = false;
        this.shouldMirrorPlayer = false;
        this.shouldEvade = false;
      }
    }

    this.updateMovement(time, delta);

    // LootDrop State Update.
    if (this.phase == ActionPhase.LootDrop && this.isPhaseInProgress) {
      this.updateWeapon(time, delta);
      if (!this.weaponIsPreparingToFire && !this.weaponIsFiring) this.updateLootDrop(time, delta);

      // LootDrop State Exit.
      if (!this.canSpawnLoot && !this.weaponIsFiring) {
        this.isPhaseInProgress = false;
        this.phase == ActionPhase.Default;

        this.scene.events.emit(AIBoss.events.StoppedSpawningLoot);
      }
    }

    // Ramp State Update.
    if (this.phase == ActionPhase.Ramp && this.isPhaseInProgress) {
      // Ramp State Exit.
      if (this.hasJumped) {
        this.isPhaseInProgress = false;
        this.phase == ActionPhase.Default;

        this.vehicle.CharacterSpine.off(CharacterSpine.events.RampInEnded, this.dropSuperMoneyBag, this);
        this.vehicle.CharacterSpine.off(CharacterSpine.events.RampOutEnded, this.onRampJumpEnded, this);
        this.vehicle.off(Vehicle.events.RingEnter, this.dropSuperMoneyBagRing, this);
        this.vehicle.off(Vehicle.events.RingExit, this.onRampJumpEnded, this);
      }
    }
  }

  protected updateMovement(time: number, delta: number) {
    if (this.shouldEvade) {
      this.moveTimer -= delta;

      if (this.moveTimer <= 0) {
        this.moveTimer = Phaser.Math.Between(this.minMoveTime, this.maxMoveTime);
        if (this.moveDirection == MoveDirection.Up) {
          this.moveDirection = MoveDirection.Down;
        } else if (this.moveDirection == MoveDirection.Down) {
          this.moveDirection = MoveDirection.Up;
        }
      }

      if (this.moveDirection == MoveDirection.Up) {
        if (!LevelUtil.getIsAtTopOfRoad(this.vehicle.y, this.vehicle.RoadDetails)) {
          this.vehicle.moveUpRoad();
        } else {
          this.moveDirection = MoveDirection.Down;
          this.vehicle.moveDownRoad();
        }
      } else if (this.moveDirection == MoveDirection.Down) {
        if (!LevelUtil.getIsAtBottomOfRoad(this.vehicle.y, this.vehicle.RoadDetails)) {
          this.vehicle.moveDownRoad();
        } else {
          this.moveDirection = MoveDirection.Up;
          this.vehicle.moveUpRoad();
        }
      }
    } else if (this.shouldMirrorPlayer) {
      this.mirrorPlayer();
    } else if (this.shouldFollowTarget) {
    } else if (!this.weaponIsPreparingToFire && !this.weaponIsFiring) {
      this.vehicle.moveToY(LevelUtil.RoadMiddle);
    }
  }

  protected mirrorPlayer() {
    this.vehicle.moveToY(this.vehicleManager.HeroVehicle?.y ?? 0);

    // let diff = (this.vehicleManager.HeroVehicle?.y ?? 0) - this.bossVehicle.y;

    // if (diff < 0) {
    //   this.bossVehicle.moveUpRoad();
    // } else if (diff > 0) {
    //   this.bossVehicle.moveDownRoad();
    // }
  }

  protected updateLootDrop(time: number, delta: number) {
    if (this.canSpawnLoot && this.vehicle.IsMoving) {
      this.spawnTimer -= delta;

      if (this.spawnTimer <= 0) {
        this.spawnTimer = this.spawnTime;

        if (this.spawnedTotal % this.moneyBagInterval == this.moneyBagInterval - 1)
          this.levelManager.spawnBehindVehicle(this.vehicle, EntityType.MoneyBag, this.vehicle.MoneySpawnOffsetX, this.vehicle.MoneySpawnOffsetY);
        else this.levelManager.spawnBehindVehicle(this.vehicle, EntityType.MoneyBills, this.vehicle.MoneySpawnOffsetX, this.vehicle.MoneySpawnOffsetY);

        this.spawnedCount++;
        this.spawnedTotal++;
      }

      if (this.spawnedTotal == this.quantityToSpawn) {
        this.canSpawnLoot = false;
        this.shouldEvade = false;
        this.weaponIsPreparingToFire = false;
        this.canFireWeapon = false;
      }
    }
  }

  protected updateWeapon(time: number, delta: number) {
    if (this.canFireWeapon && !this.weaponIsPreparingToFire && !this.weaponIsFiring) {
      if (this.shouldTriggerOnDrops) {
        let index = this.weaponTriggerPointIndex % this.weaponTriggerPoints.length;
        if (this.spawnedCount != 0 && this.spawnedCount % this.weaponTriggerPoints[index] == 0) {
          this.spawnedCount = 0;
          this.weaponTriggerPointIndex++;
          this.shouldEvade = false;

          this.weaponIsPreparingToFire = true;
          this.fireWeapon();
        }
      } else {
        this.weaponCooldown -= delta;

        if (this.weaponCooldown <= 0) {
          this.weaponIsPreparingToFire = true;
          // this.weaponIsFiring = true;
          this.fireWeapon();
        }
      }
    }
  }

  protected fireWeapon() {
    this.scene.time.delayedCall(700, () => {
      // Check if we still want to fire.
      if (!this.weaponIsPreparingToFire) return;

      this.vehicle.CharacterSpine.off(CharacterSpine.events.WarningEnded, this.onWarningFinished, this);
      this.vehicle.CharacterSpine.once(CharacterSpine.events.WarningEnded, this.onWarningFinished, this);
      this.onWarningStart();
      this.vehicle.startWarning();
    });
  }

  protected onWarningStart() {
    this.weaponIsFiring = true;
    this.weaponIsPreparingToFire = false;
  }

  protected onWarningFinished() {
    this.vehicle.fire();
    this.weaponCooldown = this.weaponCooldownTime;
  }

  protected onWeaponFiring() {
    // this.weaponIsFiring = true;
    // this.weaponIsPreparingToFire = false;
  }

  protected onWeaponFinishedFiring() {
    this.weaponIsFiring = false;
    this.shouldEvade = true;
  }

  protected dropSuperMoneyBag() {
    this.scene.time.delayedCall(500, () => {
      this.levelManager.spawnBehindVehicle(this.vehicle, EntityType.SuperMoneyBag, this.vehicle.PresentSpawnOffsetX, this.vehicle.PresentSpawnOffsetY);
    });
  }

  protected dropSuperMoneyBagRing() {
    this.scene.time.delayedCall(250, () => {
      this.levelManager.spawnBehindVehicle(this.vehicle, EntityType.SuperMoneyBag, this.vehicle.PresentSpawnOffsetX, this.vehicle.PresentSpawnOffsetY);
    });
  }

  protected onRampJumpEnded() {
    this.hasJumped = true;
  }

  protected animationStart(entry: spine.TrackEntry) {
    if (entry.animation.name == CharacterAnimation.Warning) {
      this.weaponIsFiring = true;
      this.weaponIsPreparingToFire = false;
    }
  }

  protected animationEnd(entry: spine.TrackEntry) {
    if (entry.animation.name == CharacterAnimation.Weapon) {
      this.weaponIsFiring = false;
      this.shouldEvade = true;
    }
  }
}
